import { Lock, LockOpen } from "lucide-react";
import { useState } from "react";
import {
	resolveWallHeightM,
	WALL_H_MAX_M,
	WALL_H_MIN_M,
	WALL_H_STEP_M,
} from "../../domain/geometry/buildingDimensions";
import {
	CLIMATE_PROFILE_OPTIONS,
	type ClimateProfile,
	type LocationMode,
	type Market,
} from "../../domain/location/types";
import { getDimensionLimits } from "../../domain/geometry/dimensionLimits";
import type { SaunaDimensions } from "../../engines/types";
import { useT } from "../../i18n";
import type { SaunaType } from "../../types/design";
import { HelpHint } from "../ui/HelpHint";
import { UnitSystemToggle } from "./UnitSystemToggle";

interface DimensionSlidersProps {
	/** Picks the footprint limits (issue 157). */
	saunaType: SaunaType;
	dimensions: SaunaDimensions;
	market: Market;
	locationMode: LocationMode;
	climateProfile: ClimateProfile;
	postalCode: string;
	onDimensionsChange: (d: SaunaDimensions) => void;
	onClimateProfileChange: (profile: ClimateProfile) => void;
	onPostalCodeChange: (code: string) => void;
	/** Chosen wall height (issue 155); `null` = the default. */
	wallHeightM?: number | null;
	/** Omit for a sauna without walls to raise (the barrel): no slider. */
	onWallHeightChange?: (wallHeightM: number) => void;
}

const STEP = 0.1;

function clamp(v: number, min: number, max: number) {
	return Math.round(Math.max(min, Math.min(max, v)) * 10) / 10;
}

type ClampField = "area" | "length" | "width";

interface ClampHint {
	field: ClampField;
	/** Which end of the range the typed value hit. */
	bound: "min" | "max";
	/** The value actually applied after clamping (m for dims, m² for area). */
	appliedValue: number;
	/** True when this limit maps to a rule (surfaces a link to Regler). */
	fromRule: boolean;
}

/**
 * Detects whether a *typed* value fell outside a range and had to be clamped.
 * Returns the hint payload, or null when the value was in range. A small
 * epsilon absorbs the 0.1 rounding the clamp applies so an in-range value
 * never trips the hint. Slider drags never call this — the browser keeps a
 * range input inside min/max, so only corrected text input reaches here.
 */
function detectClamp(
	requested: number,
	min: number,
	max: number,
): { bound: "min" | "max" } | null {
	const EPSILON = 0.05;
	if (requested < min - EPSILON) return { bound: "min" };
	if (requested > max + EPSILON) return { bound: "max" };
	return null;
}

const formatMm = (m: number) =>
	Math.round(m * 1000)
		.toString()
		.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

export function DimensionSliders({
	saunaType,
	dimensions,
	market,
	locationMode,
	climateProfile,
	postalCode,
	onDimensionsChange,
	onClimateProfileChange,
	onPostalCodeChange,
	wallHeightM,
	onWallHeightChange,
}: DimensionSlidersProps) {
	const t = useT();
	// The limits live in the domain (issue 157); these are just local names.
	const limits = getDimensionLimits(saunaType);
	const MIN_LENGTH = limits.length.min;
	const MAX_LENGTH = limits.length.max;
	const MIN_WIDTH = limits.width.min;
	const MAX_WIDTH = limits.width.max;
	const MIN_AREA = limits.area.min;
	const MAX_AREA = limits.area.max;
	const [areaLocked, setAreaLocked] = useState(false);
	const [lockedArea, setLockedArea] = useState(
		dimensions.width * dimensions.length,
	);
	// Local-only: which field was just clamped, so we can surface an inline
	// hint. Never stored in Zustand — it would spam zundo's undo history.
	const [clampHint, setClampHint] = useState<ClampHint | null>(null);
	// Local-only draft strings for the numeric inputs. We only clamp/commit on
	// blur, so intermediate keystrokes (e.g. "3" on the way to "3000") never
	// snap the value or fire a spurious hint.
	const [draft, setDraft] = useState<Partial<Record<ClampField, string>>>({});

	const currentArea = dimensions.width * dimensions.length;

	// Slider drags stay in range by construction, so their onChange handlers
	// clear any lingering hint instead of raising one.
	const clearClampHint = () => setClampHint(null);

	const handleAreaChange = (raw: string) => {
		const area = parseFloat(raw);
		if (isNaN(area) || area <= 0) return;
		clearClampHint();
		setLockedArea(area);
		if (areaLocked) {
			const ratio = dimensions.length / dimensions.width;
			const newWidth = clamp(Math.sqrt(area / ratio), MIN_WIDTH, MAX_WIDTH);
			const newLength = clamp(area / newWidth, MIN_LENGTH, MAX_LENGTH);
			onDimensionsChange({ width: newWidth, length: newLength });
		}
	};

	// Clears a field's draft so the input falls back to the committed value.
	const clearDraft = (field: ClampField) =>
		setDraft((prev) => {
			if (prev[field] === undefined) return prev;
			const rest = { ...prev };
			delete rest[field];
			return rest;
		});

	/** Commit a typed area value (m²) on blur, flagging any clamp. */
	const handleAreaCommit = (raw: string) => {
		clearDraft("area");
		const area = parseFloat(raw);
		if (isNaN(area)) return;
		const detected = detectClamp(area, MIN_AREA, MAX_AREA);
		if (!detected) {
			handleAreaChange(String(area));
			return;
		}
		// Apply the clamped value inline; don't route through handleAreaChange,
		// which clears the hint we're about to set.
		const applied = clamp(area, MIN_AREA, MAX_AREA);
		setLockedArea(applied);
		if (areaLocked) {
			const ratio = dimensions.length / dimensions.width;
			const newWidth = clamp(Math.sqrt(applied / ratio), MIN_WIDTH, MAX_WIDTH);
			const newLength = clamp(applied / newWidth, MIN_LENGTH, MAX_LENGTH);
			onDimensionsChange({ width: newWidth, length: newLength });
		}
		setClampHint({
			field: "area",
			bound: detected.bound,
			appliedValue: applied,
			fromRule: false,
		});
	};

	/** Commit a typed length/width value (mm) on blur, flagging clamps. */
	const handleDimensionCommit = (key: "length" | "width", raw: string) => {
		clearDraft(key);
		const mm = parseFloat(raw);
		if (isNaN(mm)) return;
		const meters = mm / 1000;
		const min = key === "length" ? MIN_LENGTH : MIN_WIDTH;
		const max = key === "length" ? MAX_LENGTH : MAX_WIDTH;
		const detected = detectClamp(meters, min, max);
		if (detected) {
			const applied = clamp(meters, min, max);
			handleSlider(key, String(applied));
			setClampHint({
				field: key,
				bound: detected.bound,
				appliedValue: applied,
				fromRule: false,
			});
			return;
		}
		clearClampHint();
		handleSlider(key, String(meters));
	};

	/** Renders the inline clamp hint for a given field, or nothing. */
	const renderClampHint = (field: ClampField) => {
		if (!clampHint || clampHint.field !== field) return null;
		const isArea = field === "area";
		const displayValue = isArea
			? `${clampHint.appliedValue.toFixed(1)} m²`
			: `${Math.round(clampHint.appliedValue * 1000)} mm`;
		const message = (
			clampHint.bound === "max" ? t.slider_clampMax : t.slider_clampMin
		).replace("{value}", displayValue);
		return (
			<p
				id={`clamp-hint-${field}`}
				className="slider-clamp-hint"
				role="status"
				aria-live="polite"
			>
				{message}
				{clampHint.fromRule && market === "se" ? (
					<>
						{" "}
						<a
							className="slider-clamp-hint__link"
							href="/guide/bygglov-bastu"
							target="_blank"
							rel="noopener noreferrer"
						>
							{t.slider_clampSeeRules}
						</a>
					</>
				) : null}
			</p>
		);
	};

	const toggleAreaLock = () => {
		if (!areaLocked) {
			setLockedArea(Math.round(currentArea * 10) / 10);
		}
		setAreaLocked(!areaLocked);
	};

	const handleSlider = (key: keyof SaunaDimensions, raw: string) => {
		const val = parseFloat(raw);
		if (isNaN(val)) return;

		if (areaLocked) {
			if (key === "length") {
				const newLen = clamp(val, MIN_LENGTH, MAX_LENGTH);
				const newWid = clamp(lockedArea / newLen, MIN_WIDTH, MAX_WIDTH);
				onDimensionsChange({ length: newLen, width: newWid });
			} else {
				const newWid = clamp(val, MIN_WIDTH, MAX_WIDTH);
				const newLen = clamp(lockedArea / newWid, MIN_LENGTH, MAX_LENGTH);
				onDimensionsChange({ width: newWid, length: newLen });
			}
		} else {
			onDimensionsChange({
				...dimensions,
				[key]: clamp(
					val,
					key === "length" ? MIN_LENGTH : MIN_WIDTH,
					key === "length" ? MAX_LENGTH : MAX_WIDTH,
				),
			});
		}
	};

	const resetDimensions = () => {
		setAreaLocked(false);
		onDimensionsChange({ width: 2.5, length: 2.5 });
	};

	return (
		<div className="inspector-section-body">
			<div className="inspector-section-toolbar">
				<span className="section-label">{t.slider_sectionLabel}</span>
				<button
					type="button"
					className="section-action"
					onClick={resetDimensions}
				>
					{t.slider_reset}
				</button>
			</div>
			{/* Intl-only: metric <-> imperial. Drives the 3D measurement read-outs
			    (dimension inputs stay metric for now). */}
			<UnitSystemToggle />
			<div className="builder-controls">
				<div className="slider-row area-row">
					<div className="slider-label-row">
						<span className="slider-label slider-label--with-hint">
							{t.slider_areaLabel}
							<HelpHint
								title={t.helpHint_areaTitle}
								body={t.helpHint_areaBody}
								learnMoreHref={
									market === "se" ? "/guide/bygglov-bastu" : undefined
								}
							/>
						</span>
						<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
							<input
								type="number"
								className="slider-value slider-value--input"
								inputMode="decimal"
								step={0.5}
								aria-label={t.slider_areaAriaLabel}
								aria-describedby={
									clampHint?.field === "area" ? "clamp-hint-area" : undefined
								}
								value={
									draft.area ??
									(areaLocked ? lockedArea : currentArea).toFixed(1)
								}
								onChange={(e) =>
									setDraft((prev) => ({ ...prev, area: e.target.value }))
								}
								onBlur={(e) => handleAreaCommit(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") e.currentTarget.blur();
								}}
							/>
							<button
								type="button"
								className={`area-lock-btn ${areaLocked ? "locked" : ""}`}
								onClick={toggleAreaLock}
								aria-label={
									areaLocked ? t.slider_unlockArea : t.slider_lockArea
								}
								title={areaLocked ? t.slider_unlockArea : t.slider_lockArea}
							>
								{areaLocked ? (
									<Lock size={14} strokeWidth={2} aria-hidden="true" />
								) : (
									<LockOpen size={14} strokeWidth={2} aria-hidden="true" />
								)}
							</button>
						</div>
					</div>
					<input
						type="range"
						className="slider-track"
						aria-label={t.slider_areaAriaLabel}
						min={MIN_AREA}
						max={MAX_AREA}
						step={0.5}
						value={areaLocked ? lockedArea : currentArea}
						onChange={(e) => handleAreaChange(e.target.value)}
					/>
					{renderClampHint("area")}
					<div className="slider-range-labels">
						<span>{MIN_AREA.toFixed(1)} m²</span>
						<span>{MAX_AREA.toFixed(1)} m²</span>
					</div>
				</div>

				{/* Width slider */}
				<div className="slider-row">
					<div className="slider-label-row">
						<span className="slider-label slider-label--with-hint">
							{t.slider_widthLabel}
							<HelpHint
								title={t.helpHint_widthTitle}
								body={t.helpHint_widthBody}
							/>
						</span>
						<input
							type="number"
							className="slider-value slider-value--input"
							inputMode="numeric"
							step={STEP * 1000}
							aria-label={t.slider_widthAriaLabel.replace(
								"{value}",
								String(Math.round(dimensions.width * 1000)),
							)}
							aria-describedby={
								clampHint?.field === "width" ? "clamp-hint-width" : undefined
							}
							value={draft.width ?? Math.round(dimensions.width * 1000)}
							onChange={(e) =>
								setDraft((prev) => ({ ...prev, width: e.target.value }))
							}
							onBlur={(e) => handleDimensionCommit("width", e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") e.currentTarget.blur();
							}}
						/>
					</div>
					<input
						type="range"
						className="slider-track"
						aria-label={t.slider_widthAriaLabel.replace(
							"{value}",
							String(Math.round(dimensions.width * 1000)),
						)}
						min={MIN_WIDTH}
						max={MAX_WIDTH}
						step={STEP}
						value={dimensions.width}
						// The value is metres; speak it in the millimetres the label shows.
						aria-valuetext={`${Math.round(dimensions.width * 1000)} mm`}
						onChange={(e) => {
							clearClampHint();
							handleSlider("width", e.target.value);
						}}
					/>
					<div className="slider-range-labels">
						<span>{formatMm(MIN_WIDTH)} mm</span>
						<span>{formatMm(MAX_WIDTH)} mm</span>
					</div>
					{renderClampHint("width")}
				</div>

				{/* Length slider */}
				<div className="slider-row">
					<div className="slider-label-row">
						<span className="slider-label slider-label--with-hint">
							{t.slider_lengthLabel}
							<HelpHint
								title={t.helpHint_lengthTitle}
								body={t.helpHint_lengthBody}
							/>
						</span>
						<input
							type="number"
							className="slider-value slider-value--input"
							inputMode="numeric"
							step={STEP * 1000}
							aria-label={t.slider_lengthAriaLabel.replace(
								"{value}",
								String(Math.round(dimensions.length * 1000)),
							)}
							aria-describedby={
								clampHint?.field === "length" ? "clamp-hint-length" : undefined
							}
							value={draft.length ?? Math.round(dimensions.length * 1000)}
							onChange={(e) =>
								setDraft((prev) => ({ ...prev, length: e.target.value }))
							}
							onBlur={(e) => handleDimensionCommit("length", e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") e.currentTarget.blur();
							}}
						/>
					</div>
					<input
						type="range"
						className="slider-track"
						aria-label={t.slider_lengthAriaLabel.replace(
							"{value}",
							String(Math.round(dimensions.length * 1000)),
						)}
						min={MIN_LENGTH}
						max={MAX_LENGTH}
						step={STEP}
						value={dimensions.length}
						// The value is metres; speak it in the millimetres the label shows.
						aria-valuetext={`${Math.round(dimensions.length * 1000)} mm`}
						onChange={(e) => {
							clearClampHint();
							handleSlider("length", e.target.value);
						}}
					/>
					<div className="slider-range-labels">
						<span>{formatMm(MIN_LENGTH)} mm</span>
						<span>{formatMm(MAX_LENGTH)} mm</span>
					</div>
					{renderClampHint("length")}
				</div>

				{onWallHeightChange ? (
					<div className="slider-row">
						<div className="slider-label-row">
							<span className="slider-label slider-label--with-hint">
								{t.dim_wallHeightLabel}
								<HelpHint
									title={t.helpHint_wallHeightTitle}
									body={t.helpHint_wallHeightBody}
								/>
							</span>
							<span className="slider-value">
								{Math.round(resolveWallHeightM(wallHeightM) * 1000)} mm
							</span>
						</div>
						<input
							type="range"
							className="slider-track"
							aria-label={t.dim_wallHeightLabel}
							// The value is metres; say it the way the label shows it.
							aria-valuetext={`${Math.round(resolveWallHeightM(wallHeightM) * 1000)} mm`}
							min={WALL_H_MIN_M}
							max={WALL_H_MAX_M}
							step={WALL_H_STEP_M}
							value={resolveWallHeightM(wallHeightM)}
							onChange={(e) => onWallHeightChange(Number(e.target.value))}
						/>
						<div className="slider-range-labels">
							<span>{Math.round(WALL_H_MIN_M * 1000)} mm</span>
							<span>{Math.round(WALL_H_MAX_M * 1000)} mm</span>
						</div>
					</div>
				) : null}

				{market === "intl" || locationMode === "manual_climate" ? (
					<div className="slider-row">
						<div
							className="slider-label-row slider-label-row--inline"
							style={{ marginBottom: "8px" }}
						>
							<label htmlFor="climate-zone" className="slider-label">
								{t.slider_climateZoneLabel}
							</label>
							<HelpHint
								title={t.helpHint_climateZoneTitle}
								body={t.helpHint_climateZoneBody}
								learnMoreHref={
									market === "se" ? "/guide/snozon-bastu" : undefined
								}
							/>
						</div>
						<select
							id="climate-zone"
							aria-label={t.slider_climateZoneLabel}
							value={climateProfile.snowLoadClass}
							onChange={(event) => {
								const next =
									CLIMATE_PROFILE_OPTIONS.find(
										(option) => option.snowLoadClass === event.target.value,
									) ?? CLIMATE_PROFILE_OPTIONS[1];
								onClimateProfileChange({
									snowLoadClass: next.snowLoadClass,
									snowZone: next.snowZone,
									windExposure: climateProfile.windExposure ?? "normal",
									...(climateProfile.elevationBand
										? { elevationBand: climateProfile.elevationBand }
										: {}),
								});
							}}
							className="inspector-text-input"
						>
							{CLIMATE_PROFILE_OPTIONS.map((option) => (
								<option key={option.snowLoadClass} value={option.snowLoadClass}>
									{option.label}
								</option>
							))}
						</select>
						<div className="slider-range-labels" style={{ marginTop: "8px" }}>
							<span>
								{
									CLIMATE_PROFILE_OPTIONS.find(
										(option) =>
											option.snowLoadClass === climateProfile.snowLoadClass,
									)?.description
								}
							</span>
						</div>
					</div>
				) : (
					<div className="slider-row">
						<div
							className="slider-label-row slider-label-row--inline"
							style={{ marginBottom: "8px" }}
						>
							<label htmlFor="postal-code" className="slider-label">
								{t.slider_postalCodeLabel}
							</label>
							<HelpHint
								title={t.helpHint_postalCodeTitle}
								body={t.helpHint_postalCodeBody}
								learnMoreHref="/guide/snozon-bastu"
							/>
						</div>
						<input
							id="postal-code"
							type="text"
							inputMode="numeric"
							maxLength={6}
							placeholder={t.slider_postalCodePlaceholder}
							value={postalCode}
							onChange={(e) => onPostalCodeChange(e.target.value)}
							className="inspector-text-input"
						/>
					</div>
				)}
			</div>
		</div>
	);
}
